import { clamp } from 'lodash'

import { DECIMAL_REGEX, INTEGER_REGEX } from '@/constants'

import { NumericInputMode } from './numeric-input.types'

export const isValidNumericValue = (value: string, mode: NumericInputMode): boolean => {
  const regex = mode === 'decimal' ? DECIMAL_REGEX : INTEGER_REGEX

  // Note: Reject values starting with a dot (e.g., ".5" is invalid, use "0.5" instead)
  if (value.startsWith('.')) {
    return false
  }

  return regex.test(value)
}

export const removeLeadingZeros = (value: string, isDecimalMode: boolean): string => {
  if (value.length <= 1) {
    return value
  }

  // Note: Allow "0." prefix for decimal mode (e.g., "0.5")
  if (isDecimalMode && value.charAt(0) === '0' && value.charAt(1) !== '.') {
    return value.slice(1)
  }

  if (!isDecimalMode && value.charAt(0) === '0') {
    return value.slice(1)
  }

  return value
}

export const limitDecimalPlaces = (value: string, decimalPlaces: number): string => {
  if (!value.includes('.')) {
    return value
  }

  const [integerPart, decimalPart] = value.split('.')

  if (decimalPart && decimalPart.length > decimalPlaces) {
    return `${integerPart}.${decimalPart.slice(0, decimalPlaces)}`
  }

  return value
}

export const clampValue = (
  value: string,
  min: number | undefined,
  max: number | undefined,
  isDecimalMode: boolean,
): string => {
  const parsedValue = isDecimalMode ? parseFloat(value) : parseInt(value, 10)

  if (isNaN(parsedValue)) {
    return value
  }

  const lowerBound = min ?? -Infinity
  const upperBound = max ?? Infinity
  const clampedValue = clamp(parsedValue, lowerBound, upperBound)

  // Note: (e.g., "1." should stay "1.", not become "1")
  if (clampedValue === parsedValue) {
    return value
  }

  return clampedValue.toString()
}

export const processNumericValue = (
  newValue: string,
  currentValue: string | undefined,
  mode: NumericInputMode,
  decimalPlaces: number,
  min: number | undefined,
  max: number | undefined,
): string => {
  const isDecimalMode = mode === 'decimal'

  if (!isValidNumericValue(newValue, mode)) {
    return currentValue ?? ''
  }

  let processedValue = removeLeadingZeros(newValue, isDecimalMode)

  if (isDecimalMode) {
    processedValue = limitDecimalPlaces(processedValue, decimalPlaces)
  }

  processedValue = clampValue(processedValue, min, max, isDecimalMode)

  return processedValue
}
